// src/App.js
import React, { useContext, useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { AuthContext, AuthProvider } from './components/AuthContext';
import { Toaster } from 'sonner';
import axiosInstance from './services/axiosInstance';
import AppLayout from './components/AppLayout';
import CreateLeague from './components/CreateLeague';
import LeagueDetail from './components/LeagueDetails';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import InvitePage from './components/InvitePage';
import Profile from './components/Profile';
import Achievements from './components/UserAchievements';
import MatchesList from './components/MatchesList';
import MatchDetails from './components/MatchDetails';
import CreateMatch from './components/CreateMatch';
import EditMatch from './components/EditMatch';
import UpdateMatchData from './components/UpdateMatchData';
import ImportMatches from './components/ImportMatches';
import TeamsList from './components/TeamsList';
import CreateTeam from './components/CreateTeam';
import Rules from './components/Rules';
import Scoring from './components/Scoring';
import Leaderboard from './components/Leaderboard';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import UnifiedLeagueList from './components/UnifiedLeagueList';
import LandingPage from './components/LandingPage';
import LegalPage from './components/LegalPage';
import AdminDashboard from './components/AdminDashboard';
import CreateTournament from './components/CreateTournament';
import UpdateTournament from './components/UpdateTournament';
import TournamentRoundMultipliers from './components/TournamentRoundMultipliers';
import Contact from './components/Contact';
import './i18n';
import './styles/App.css';

function AppContent() {
	const { isLoggedIn, loading } = useContext(AuthContext);
	const location = useLocation(); //

	// Funkce pro kontrolu, zda se jedná o autentizační stránku nebo stránku pozvánky
	const isAuthPage = () => {
		return location.pathname === '/login' ||
			location.pathname === '/register' ||
			location.pathname === '/forgot-password' ||
			location.pathname === '/reset-password' ||
			location.pathname === '/legal' ||
			location.pathname === '/contact' ||
			location.pathname.startsWith('/invite/league');
	};

	if (loading) { // Render nothing or a loading spinner while authenticating
		return null; // Můžete zde přidat komponentu pro načítání, např. <LoadingSpinner />
	}

	// Přesměrování po úspěšném přihlášení/registraci
	if (isLoggedIn && (location.pathname === '/login' || location.pathname === '/register')) { //
		return <Navigate to="/dashboard" replace />;
	}

	if (!isLoggedIn && !isAuthPage() && location.pathname !== '/') {
		return <Navigate to="/login" replace />;
	}

	if (location.pathname === '/') {
		if (isLoggedIn) {
			return <Navigate to="/dashboard" replace />;
		}
		return <LandingPage />;
	}

	return (
		<div className="app-container">
			{isAuthPage() ? (
				// Routy pro autentizaci a pozvánky (bez postranního panelu a headeru)
				<div className="auth-content">
					<Routes>
						<Route path="/login" element={<LoginForm />} />
						<Route path="/register" element={<RegisterForm />} />
						<Route path="/forgot-password" element={<ForgotPassword />} />
						<Route path="/reset-password" element={<ResetPassword />} />
						<Route path="/legal" element={<LegalPage />} />
						<Route path="/contact" element={<Contact />} />
						<Route path="/invite/league/:leagueId/:inviteToken" element={<InvitePage />} />
					</Routes>
				</div>
			) : (
				// Ostatní routy s AppLayout (header + sidebar + content)
				<Routes>
					<Route path="/" element={<AppLayout />}>
						{/* Zde budou routy, které používají AppLayout */}
						{/* Nastavujeme výchozí stránku po přihlášení */}
						<Route index element={<Navigate to={isLoggedIn ? "/dashboard" : "/"} replace />} />

						<Route path="dashboard" element={<UnifiedLeagueList mode="my" />} />
						<Route path="/contact" element={<Contact />} />
						<Route path="public-leagues" element={<UnifiedLeagueList mode="public" computeUserRankings />} />
						<Route path="create-league" element={<CreateLeague />} />
						<Route path="leaderboard" element={<Leaderboard />} />
						<Route
							path="league/:leagueId"
							element={
								isLoggedIn ? ( //
									<LeagueDetail />
								) : (
									<Navigate to="/login" replace /> //
								)
							}
						/>

						{/* Routy pro profil a úspěchy */}
						<Route path="profile" element={<Profile />} />
						<Route path="achievements" element={<Achievements />} />

						{/* Admin routy (pokud RoleProtectedRoute existuje a je potřeba) */}
						<Route
							path="matches"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<MatchesList />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="match-details/:id"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<MatchDetails />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="admin/create-match"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<CreateMatch />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="admin/import-matches"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<ImportMatches />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="admin/edit-match"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<EditMatch />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="admin/update-match-data"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<UpdateMatchData />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="teams"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<TeamsList />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="admin/create-team"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<CreateTeam />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="admin/create-tournament"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<CreateTournament />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="admin/update-tournament"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<UpdateTournament />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="admin/tournament-rounds"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<TournamentRoundMultipliers />
								</RoleProtectedRoute>
							}
						/>
						<Route
							path="/admin/dashboard"
							element={
								<RoleProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
									<AdminDashboard />
								</RoleProtectedRoute>
							}
						/>

						{/* Statické stránky */}
						<Route path="rules" element={<Rules />} />
						<Route path="scoring" element={<Scoring />} />
						<Route path="faq" element={<div>Stránka FAQ</div>} />
					</Route>
				</Routes>
			)}
		</div>
	);
}

function App() {

	useEffect(() => {
		axiosInstance.get('/auth/csrf').catch(() => {
			console.warn('CSRF init failed (ignore in dev)');
		});
	}, []);

	return (
		<AuthProvider>
			<BrowserRouter>
				<AppContent />
				<Toaster
					position="top-right"
					richColors
					closeButton
				/>
			</BrowserRouter>
		</AuthProvider>
	);
}

export default App;
